/**
 * The app config from the server (docs/redesign-2026-11/resilience.md §4): fetched at start and on window focus (at
 * most once a minute), the last good copy kept in localStorage and used offline. Until anything arrives the
 * built-in defaults (today's values) apply, so a failed fetch changes nothing.
 *
 * `WEB_JS_VERSION` is the web bundle's integer version. Bump it in a release that needs the server to stop serving
 * older bundles (the server's `min_version.web.js`): tabs still on an older bundle then show "A new version of the
 * site is ready · Reload".
 */
import { DEFAULT_APP_CONFIG, normalizeAppConfig, type AppConfig } from '@robotensor/api'
import { useSyncExternalStore } from 'react'

export const WEB_JS_VERSION = 1

export const CONFIG_KEY = 'rt.appConfig.v1'
/** Window focus refetches at most this often. */
export const FOCUS_REFRESH_MS = 60_000

export interface ConfigState {
  config: AppConfig
  /** Where the config came from: built-in defaults, the last good copy, or the server just now. */
  source: 'default' | 'stored' | 'server'
  fetchedAt: number | null
}

type Fetcher = () => Promise<unknown>

/** A config store; one per tab in the app (`appConfig` below), separate ones in tests. */
export function createConfigStore(fetcher: Fetcher, storage: () => Storage | null = () => globalThis.localStorage ?? null, now: () => number = Date.now) {
  let state: ConfigState = load()
  let inflight: Promise<void> | null = null
  let lastAttempt = 0
  const listeners = new Set<() => void>()

  function load(): ConfigState {
    try {
      const raw = storage()?.getItem(CONFIG_KEY)
      if (raw) {
        const saved = JSON.parse(raw) as { config?: unknown; fetchedAt?: number }
        return { config: normalizeAppConfig(saved.config), source: 'stored', fetchedAt: typeof saved.fetchedAt === 'number' ? saved.fetchedAt : null }
      }
    } catch {
      // private window, blocked storage or a broken copy: defaults
    }
    return { config: DEFAULT_APP_CONFIG, source: 'default', fetchedAt: null }
  }

  function set(next: ConfigState) {
    state = next
    for (const l of listeners) l()
  }

  /** Fetches now unless one is running; `{ifStale: true}` skips it when the last try was under a minute ago. */
  function refresh(opts: { ifStale?: boolean } = {}): Promise<void> {
    if (inflight) return inflight
    if (opts.ifStale && now() - lastAttempt < FOCUS_REFRESH_MS) return Promise.resolve()
    lastAttempt = now()
    inflight = fetcher()
      .then((raw) => {
        const config = normalizeAppConfig(raw)
        const fetchedAt = now()
        try {
          storage()?.setItem(CONFIG_KEY, JSON.stringify({ config, fetchedAt }))
        } catch {
          // storage full or blocked: the copy in memory still applies
        }
        set({ config, source: 'server', fetchedAt })
      })
      .catch(() => {
        // offline or an older platform: keep what we have
      })
      .finally(() => {
        inflight = null
      })
    return inflight
  }

  return {
    get: () => state,
    subscribe(l: () => void) {
      listeners.add(l)
      return () => void listeners.delete(l)
    },
    refresh,
    /** Fetch at start and on window focus (throttled); returns the cleanup. */
    start(win: Pick<Window, 'addEventListener' | 'removeEventListener'> | null = typeof window === 'undefined' ? null : window): () => void {
      void refresh()
      if (!win) return () => undefined
      const onFocus = () => void refresh({ ifStale: true })
      win.addEventListener('focus', onFocus)
      return () => win.removeEventListener('focus', onFocus)
    },
    /** Tests only. */
    reset() {
      lastAttempt = 0
      set(load())
    },
  }
}

export type ConfigStore = ReturnType<typeof createConfigStore>

async function fetchConfig(): Promise<unknown> {
  const r = await fetch('/api/platform/v1/app/config', { headers: { accept: 'application/json' }, credentials: 'same-origin' })
  if (!r.ok) throw new Error(`app config: HTTP ${r.status}`)
  return r.json()
}

/** The tab's config store. */
export const appConfig = createConfigStore(fetchConfig)

export function useConfigState(store: ConfigStore = appConfig): ConfigState {
  return useSyncExternalStore(store.subscribe, store.get, store.get)
}

/** The current config (defaults until the server answers). */
export function useAppConfig(store: ConfigStore = appConfig): AppConfig {
  return useConfigState(store).config
}

/** Rules that aren't React-bound (pure helpers) read the config through this. */
export const currentConfig = (): AppConfig => appConfig.get().config
